/**
 * Booking calendar — week view of available slots, grouped by tier.
 *
 * Auth-gated: anonymous students get a friendly "sign in" prompt.
 * Picking a slot → POST /api/cart/items → cart drawer toast. The user
 * continues to the cart page to checkout when ready.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useStudentAuth } from "../StudentAuthContext";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const date = new Date(d);
  const dow = (date.getUTCDay() + 6) % 7; // 0=Mon..6=Sun
  date.setUTCDate(date.getUTCDate() - dow);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function fmtDate(d) {
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

function tierStyle(tier) {
  if (tier === "evening-60") return "border-[#1C1C1C] bg-white text-[#1C1C1C]";
  if (tier === "daytime-60") return "border-[#1C1C1C]/15 bg-white text-[#1C1C1C]";
  return "border-[#1C1C1C]/10 bg-white text-[#1C1C1C]";
}

export default function Book() {
  const { status, student, token } = useStudentAuth();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [cart, setCart] = useState({ items: [], total_gbp: 0 });
  const [adding, setAdding] = useState(null); // hold of the slot start being added
  const [errMsg, setErrMsg] = useState("");

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setUTCDate(d.getUTCDate() + 7);
    return d;
  }, [weekStart]);

  async function loadSlots() {
    setLoadingSlots(true);
    setErrMsg("");
    try {
      const data = await api.listSlots(weekStart.toISOString(), weekEnd.toISOString());
      setSlots(data);
    } catch (err) {
      setErrMsg(err.message || "Could not load availability");
    } finally {
      setLoadingSlots(false);
    }
  }

  async function loadCart() {
    if (status !== "auth") return;
    try {
      const c = await api.cartGet(token);
      setCart(c);
    } catch { /* no-op */ }
  }

  useEffect(() => { loadSlots(); /* eslint-disable-next-line */ }, [weekStart]);
  useEffect(() => { loadCart(); /* eslint-disable-next-line */ }, [status, token]);

  async function addToCart(slot) {
    if (status !== "auth") {
      navigate("/login", { state: { next: "/book" } });
      return;
    }
    setAdding(slot.start);
    setErrMsg("");
    try {
      const c = await api.cartAdd(token, slot.start, slot.end);
      setCart(c);
      // Optimistic: hide that slot
      setSlots((prev) => prev.filter((s) => s.start !== slot.start));
    } catch (err) {
      setErrMsg(err.message || "Could not add to cart");
      // Refresh slots in case state diverged
      loadSlots();
    } finally {
      setAdding(null);
    }
  }

  // Group slots by day-of-week within current view
  const slotsByDay = useMemo(() => {
    const m = {};
    for (let i = 0; i < 7; i++) m[i] = [];
    for (const s of slots) {
      const d = new Date(s.start);
      const dow = (d.getUTCDay() + 6) % 7;
      m[dow].push(s);
    }
    return m;
  }, [slots]);

  const weekDates = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setUTCDate(d.getUTCDate() + i);
      out.push(d);
    }
    return out;
  }, [weekStart]);

  function nudgeWeek(delta) {
    const d = new Date(weekStart);
    d.setUTCDate(d.getUTCDate() + delta * 7);
    setWeekStart(d);
  }

  const isThisWeek =
    weekStart.toISOString().slice(0, 10) === startOfWeek(new Date()).toISOString().slice(0, 10);
  const cartCount = cart?.items?.length || 0;

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-20 md:pt-24 pb-24 px-6 md:px-10"
      data-testid="book-page"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
              Booking
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              Find a time.
            </h1>
            <p className="mt-3 text-base text-[#1C1C1C]/65">
              All times shown in your local timezone. Slots are 30 or 60 minutes.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {status === "auth" ? (
              <>
                <span className="text-xs text-[#1C1C1C]/60" data-testid="book-greeting">
                  {student?.name ? `Hi, ${student.name}` : student?.email}
                </span>
                <button
                  onClick={() => navigate("/me")}
                  data-testid="book-me-link"
                  className="px-3 py-2 text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] transition-colors"
                >
                  My lessons
                </button>
              </>
            ) : null}
            <button
              data-testid="book-cart-btn"
              onClick={() => navigate("/cart")}
              className="px-4 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
            >
              Cart {cartCount > 0 ? `· ${cartCount}` : ""}
            </button>
          </div>
        </div>

        {/* Week navigator */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => nudgeWeek(-1)}
            disabled={isThisWeek}
            className="px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/70 hover:text-[#1C1C1C] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="book-week-prev"
          >
            ← Previous
          </button>
          <div className="font-display text-xl text-[#1C1C1C]" data-testid="book-week-label">
            {fmtDate(weekStart)} — {fmtDate(new Date(weekEnd.getTime() - 1))}
          </div>
          <button
            onClick={() => nudgeWeek(1)}
            className="px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/70 hover:text-[#1C1C1C]"
            data-testid="book-week-next"
          >
            Next →
          </button>
        </div>

        {/* Tier legend */}
        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60">
          <span><span className="inline-block w-2 h-2 rounded-full bg-[#1C1C1C] mr-2 align-middle" /> Evening 60-min · £65</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-[#1C1C1C]/40 mr-2 align-middle" /> Daytime 60-min · £50</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-[#1C1C1C]/20 mr-2 align-middle" /> Daytime 30-min · £35</span>
        </div>

        {errMsg && (
          <div className="mb-4 px-4 py-3 bg-[#B83A3A]/10 text-[#B83A3A] rounded-xl text-sm" data-testid="book-error">
            {errMsg}
          </div>
        )}

        {loadingSlots ? (
          <div className="text-[#1C1C1C]/60 text-sm">Loading availability…</div>
        ) : slots.length === 0 ? (
          <div
            className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-12 text-center text-[#1C1C1C]/60"
            data-testid="book-no-slots"
          >
            No availability this week — try the next one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3 md:gap-4">
            {weekDates.map((d, i) => (
              <div
                key={i}
                data-testid={`book-day-${i}`}
                className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-3 md:p-4"
              >
                <div className="mb-3">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55">{DAY_LABELS[i]}</div>
                  <div className="font-display text-base text-[#1C1C1C]">{fmtDate(d)}</div>
                </div>
                <div className="space-y-2">
                  {slotsByDay[i].length === 0 ? (
                    <div className="text-[11px] text-[#1C1C1C]/40 italic py-2">—</div>
                  ) : (
                    slotsByDay[i].map((s) => (
                      <button
                        key={s.start}
                        data-testid={`book-slot-${s.start}`}
                        onClick={() => addToCart(s)}
                        disabled={adding === s.start}
                        className={`w-full text-left rounded-lg border px-3 py-2 transition-colors hover:bg-[#1C1C1C] hover:text-[#FAF9F6] disabled:opacity-50 ${tierStyle(s.tier_id)}`}
                      >
                        <div className="font-mono text-sm tracking-tight">
                          {fmtTime(s.start)} – {fmtTime(s.end)}
                        </div>
                        <div className="text-[10px] tracking-[0.22em] uppercase opacity-70 mt-0.5">
                          {s.tier_id.includes("30") ? "30 min" : "60 min"} · £{s.amount_gbp}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
